import React, { ReactNode, useState } from 'react';
import { Chart as ChartJS, ArcElement, Legend, Tooltip as ChartTooltip, Plugin, ChartData } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ActivityIndicator, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import Tooltip from '../../../components/Tooltip';
import Card from '../../../components/Card';

interface DoughnutDatasetWithLineColors {
    lineColors?: string[];
    backgroundColor?: string | string[];
    data: (number | null)[];
}

interface ArcElementWithProps extends ArcElement {
    hidden?: boolean;
    startAngle: number;
    endAngle: number;
    outerRadius: number;
    innerRadius: number;
}

const externalLabelsPlugin: Plugin<'doughnut'> = {
    id: 'externalLabels',
    afterDraw: (chart) => {
        const { ctx, data, chartArea } = chart;

        if (data.datasets.length === 0 || data.datasets[0].data.length === 0) {
            return;
        }

        const dataset = data.datasets[0] as DoughnutDatasetWithLineColors;
        const lineColors = dataset.lineColors || dataset.backgroundColor;

        if (!lineColors) {
            return;
        }

        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;

        data.datasets[0].data.forEach((value, index) => {
            if (value !== 0) {
                const meta = chart.getDatasetMeta(0);
                const element = meta.data[index];

                if (!element ||
                    !('startAngle' in element) ||
                    !('endAngle' in element) ||
                    !('outerRadius' in element) ||
                    !('innerRadius' in element)) {
                    return;
                }

                const arc = element as unknown as ArcElementWithProps;

                if (arc.hidden) {
                    return;
                }

                const { startAngle, endAngle, outerRadius, innerRadius } = arc;
                const midAngle = startAngle + (endAngle - startAngle) / 2;

                const lineColor = lineColors[index % lineColors.length];
                const sphereRadius = (outerRadius + innerRadius) / 2;
                const sphereX = centerX + Math.cos(midAngle) * sphereRadius;
                const sphereY = centerY + Math.sin(midAngle) * sphereRadius;

                ctx.fillStyle = lineColor;
                ctx.beginPath();
                ctx.arc(sphereX, sphereY, 4, 0, 2 * Math.PI);
                ctx.fill();

                const lineStartX = centerX + Math.cos(midAngle) * (sphereRadius + 4);
                const lineStartY = centerY + Math.sin(midAngle) * (sphereRadius + 4);

                let lineEndX: number, lineEndY: number, labelX: number, labelY: number;

                const xDirection = Math.cos(midAngle) > 0 ? 1 : -1;

                ctx.textAlign = xDirection === 1 ? 'left' : 'right';
                ctx.textBaseline = 'middle';
                ctx.font = '500 14px Open Sans';

                if (Math.abs(Math.sin(midAngle)) > 0.7) {
                    const lineCornerY = centerY + Math.sin(midAngle) * (outerRadius + 10);
                    lineEndX = centerX + xDirection * (outerRadius - 10);
                    lineEndY = lineCornerY;

                    ctx.beginPath();
                    ctx.moveTo(lineStartX, lineStartY);
                    ctx.lineTo(centerX + Math.cos(midAngle) * (outerRadius + 5), lineCornerY);
                    ctx.lineTo(lineEndX, lineEndY);

                    labelX = lineEndX + (xDirection * 5);
                    labelY = lineEndY;
                } else {
                    lineEndX = centerX + Math.cos(midAngle) * (outerRadius + 10);
                    lineEndY = centerY + Math.sin(midAngle) * (outerRadius + 10);

                    ctx.beginPath();
                    ctx.moveTo(lineStartX, lineStartY);
                    ctx.lineTo(lineEndX, lineEndY);

                    labelX = lineEndX + (xDirection * 5);
                    labelY = lineEndY;
                }

                ctx.strokeStyle = lineColor;
                ctx.lineWidth = 1.5;
                ctx.stroke();

                ctx.fillStyle = lineColor;
                const labelValue = typeof value === 'number' ? value.toLocaleString('pt-BR') : String(value);
                ctx.fillText(labelValue, labelX, labelY);
            }
        });
    }
};

ChartJS.register(ArcElement, ChartTooltip, Legend, externalLabelsPlugin);

const Header = ({ children }: { children: ReactNode; }) => {
    return (children);
};

interface Props {
    children?: ReactNode;
    labels: string[];
    chartData: number[];
    sectionColors: string[];
    linesColors?: string[];
    legendAlign?: 'top' | 'right' | 'left';
    isLoading: boolean;
}

DoughnutChart.Header = Header;

export default function DoughnutChart({ children, labels, chartData, sectionColors, linesColors, legendAlign = 'top', isLoading }: Props) {
    const { styles, theme } = useStyles(styleSheet);
    const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });

    const data: ChartData<'doughnut'> = {
        labels: labels,
        datasets: [{
            data: chartData,
            backgroundColor: sectionColors,
            borderWidth: 1,
            lineColors: linesColors,
            borderColor: sectionColors,
        }]
    };

    function limitedSizeText(text: string) {
        if (text.length > 17) {
            const truncatedText = text.length > 17 ? `${text.substring(0, 17)}...` : text;
            const tooltipDirection = legendAlign === 'right'
                ? 'left'
                : legendAlign === 'left'
                    ? 'right'
                    : 'top';
            return <Tooltip
                useSimpleBox
                style={{
                    color: theme.colors.white['500'],
                    padding: 4,
                    borderRadius: 12,
                    justifyContent: 'center',
                    zIndex: 9999,
                    background: theme.colors.neutral['500'],
                    top: tooltipDirection === 'top' ? 20 : 0
                }}
                tooltipContent={text}
                tooltipDirection={tooltipDirection}
            >
                <MyAppText style={styles.legendLabel}>{truncatedText}</MyAppText>
            </Tooltip>;
        } else {
            return <MyAppText style={styles.legendLabel}>{text}</MyAppText>;
        }
    }

    function displayLegendOnSide() {
        return <View
            style={styles.legendContainer}
        >
            {labels.map((item, index) =>
                <View key={`${item}-${index}`} style={[styles.legendItem, { zIndex: (chartData.length + 1) - index }]}>
                    <View style={styles.legendCircle(sectionColors[labels.findIndex(l => l === item)])}/>
                    {limitedSizeText(item)}
                </View>
            )}
        </View>;
    }

    return (
        <Card style={{ borderWidth: 1, borderColor: theme.colors.neutral['100'] }}>
            {children}
            {isLoading
                ? <View style={styles.allSpaceCentered}>
                    <ActivityIndicator size='large' color={theme.colors.primary['500']}/>
                </View>
                : <>
                    {chartData.length === 0
                        ? <View style={styles.allSpaceCentered}>
                            <MyAppText style={styles.noDataText}>{translate('noRecordsForSelectedPeriod')}</MyAppText>
                        </View>
                        : <>
                            {legendAlign === 'top'
                                ? <View style={styles.legendGrid}>
                                    {labels.map((item, index) =>
                                        <View key={`${item}-${index}`} style={[styles.legendItemHorizontal, { zIndex: (chartData.length + 1) - index }]}>
                                            <View style={styles.legendCircle(sectionColors[labels.findIndex(l => l === item)])}/>
                                            {limitedSizeText(item)}
                                        </View>
                                    )}
                                </View>
                                : null }
                            <View
                                style={styles.chartContainer(legendAlign === 'right' || legendAlign === 'left')}
                            >
                                {legendAlign === 'left' ? displayLegendOnSide() : null}
                                <View
                                    style={{
                                        flex: 1,
                                        height: '100%',
                                        minHeight: 200,
                                        overflow: 'hidden',
                                        position: 'relative'
                                    }}
                                    onLayout={(event) => {
                                        const { width, height } = event.nativeEvent.layout;
                                        if (width != chartDimensions.width || height != chartDimensions.height) {
                                            setChartDimensions({ width, height });
                                        }
                                    }}
                                >
                                    {(chartDimensions.width > 0 && chartDimensions.height > 0) && (
                                        <View
                                            style={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: chartDimensions.width,
                                                height: chartDimensions.height
                                            }}
                                        >
                                            <Doughnut
                                                key={`chart-${chartDimensions.width}-${chartDimensions.height}`}
                                                options={{
                                                    responsive: false,
                                                    maintainAspectRatio: false,
                                                    layout: {
                                                        padding: 30
                                                    },
                                                    plugins: {
                                                        tooltip: {
                                                            enabled: false,
                                                        },
                                                        legend: {
                                                            display: false,
                                                        }
                                                    },
                                                }}
                                                width={chartDimensions.width}
                                                height={chartDimensions.height}
                                                data={data}
                                            />
                                        </View>
                                    )}
                                </View>
                                {legendAlign === 'right' ? displayLegendOnSide() : null}
                            </View>
                        </>}
                </>}
        </Card>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    card: {
        flex: 1,
        flexDirection: 'column',
        backgroundColor: theme.colors.white['500'],
        padding: 16,
        gap: 16,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.neutral['100']
    },
    chartContainer: (legendOnSide: boolean) => ({
        flex: 1,
        flexDirection: legendOnSide ? 'row' : 'column',
        minHeight: 200,
        width: '100%',
        position: 'relative',
        zIndex: 1,
    }),
    legendGrid: {
        flexDirection: 'row',
        justifyContent: 'space-around',
        rowGap: 4,
        flexWrap: 'wrap',
        zIndex: 2
    },
    legendItem: {
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center',
    },
    legendItemHorizontal: {
        width: '30%',
        flexDirection: 'row',
        columnGap: 8,
        alignItems: 'center',
    },
    legendLabel: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400']
    },
    noDataText: {
        fontSize: 14,
        fontWeight: '500',
        color: theme.colors.neutral['400'],
        textAlign: 'center'
    },
    legendCircle: (color: string) => ({
        borderRadius: 9999,
        backgroundColor: color,
        width: 12,
        height: 12
    }),
    legendContainer: {
        flexDirection: 'column',
        rowGap: 16,
        justifyContent: 'center'
    },
    allSpaceCentered: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    }
}));
